/*
  <spin-viewer> — 360° product viewer with seed.com-style auto-spin.

  Behaviour:
    - Auto-rotates continuously on its own, before any hover or tap, and keeps
      turning while hovered — cards stay clickable links the whole time.
    - Grabbing/dragging takes over to scrub by hand; auto-spin pauses while
      you drag and resumes shortly after you let go. A drag never counts as a
      click, so scrubbing a product card doesn't open the product.
    - Honours prefers-reduced-motion (no auto-spin; drag still works).
    - Pauses when scrolled off-screen to save cycles.

  Attributes (optional, set by the Liquid snippet):
    data-autospin="false"  — disable auto-rotation (drag-only)
    data-spin-speed="350"  — ms per frame (lower = faster). Default 350, i.e.
                             one full turn every 7s with 20 frames. Keep every
                             viewer on the same value so they stay in step.
    data-spin-dir="-1"     — direction: 1 forward, -1 reverse. Default 1.

  Markup contract (rendered by snippets/spin-viewer.liquid):
    <spin-viewer>
      <div class="spin-viewer">
        <span class="spin-badge" data-spin-badge>…</span>
        <img data-spin-image …>
        <span class="spin-hint" data-spin-hint>…</span>
        <div class="spin-progress"><div class="spin-progress-fill" data-spin-fill></div></div>
      </div>
      <script type="application/json" data-spin-frames>["url1","url2",…]</script>
    </spin-viewer>
*/
class SpinViewer extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector('.spin-viewer');
    this.img = this.querySelector('[data-spin-image]');
    this.fill = this.querySelector('[data-spin-fill]');

    const framesEl = this.querySelector('[data-spin-frames]');
    try {
      this.frames = framesEl ? JSON.parse(framesEl.textContent) : [];
    } catch (e) {
      this.frames = [];
    }
    if (!this.container || !this.img) return;

    this.frameIndex = 0;
    this.dragging = false;
    this.startX = 0;

    if (this.frames.length < 2) {
      // Static fallback: no real spin sequence uploaded yet.
      this.querySelectorAll('[data-spin-badge], [data-spin-hint]').forEach((el) => el.remove());
      const progress = this.querySelector('.spin-progress');
      if (progress) progress.remove();
      this.container.style.cursor = 'default';
      return;
    }

    // Touch screens can't hover — tell them to swipe instead.
    const hint = this.querySelector('[data-spin-hint]');
    if (hint && window.matchMedia && window.matchMedia('(hover: none)').matches) {
      hint.textContent = hint.dataset.touchHint || 'Swipe to spin';
    }

    // Config
    // mode: 'auto' (always turning — default), 'hover' (only while hovered), 'off'
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mode = this.getAttribute('data-spin-mode') || 'auto';
    if (this.getAttribute('data-autospin') === 'false') this.mode = 'off';
    if (this.reduceMotion) this.mode = 'off';
    this.speed = parseInt(this.getAttribute('data-spin-speed'), 10) || 350;
    this.spinDir = parseInt(this.getAttribute('data-spin-dir'), 10) === -1 ? -1 : 1;
    this.lastTick = 0;
    // Hover mode idles until pointer enters; auto mode runs immediately.
    this.paused = this.mode !== 'auto';
    this.hovered = false;
    this.staticSrc = null; // set when a thumbnail overrides the spin (PDP)
    if (this.mode === 'hover') this.container.classList.add('spin-viewer--hoverable');

    this.preloaded = false;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.tick = this.tick.bind(this);

    this.container.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp); window.addEventListener('pointercancel', this.onUp);
    // Hover pause is a nice touch on desktop; on touch it's a no-op.
    this.container.addEventListener('pointerenter', this.onEnter);
    this.container.addEventListener('pointerleave', this.onLeave);
    // Swallow the click that follows a real drag (cards are links).
    this.container.addEventListener('click', (e) => {
      if (this.justDragged) { e.preventDefault(); e.stopPropagation(); this.justDragged = false; }
    }, true);

    // Only preload + auto-spin while near the viewport (many spinners on a
    // page shouldn't all fetch frames at once).
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        this.onScreen = entries[0].isIntersecting;
        if (this.onScreen && !this.preloaded) this.preload();
      }, { rootMargin: '300px', threshold: 0.01 });
      this.io.observe(this);
    } else {
      this.onScreen = true;
      this.preload();
    }

    this.setFrame(0);
    // Timer rather than requestAnimationFrame: rAF is throttled to ~1fps in
    // background/embedded contexts, which freezes the spin. A 40ms interval
    // is cheap and keeps rotation smooth wherever the theme is rendered.
    if (this.mode !== 'off') this.timer = setInterval(this.tick, 40);
  }

  disconnectedCallback() {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    if (this.timer) clearInterval(this.timer);
    if (this.io) this.io.disconnect();
    clearInterval(this.settleT);
    clearTimeout(this.resetT);
    clearTimeout(this.resumeT);
  }

  preload() {
    if (this.preloaded) return;
    this.preloaded = true;
    this.loaded = 0;
    this.frames.forEach((src) => {
      const i = new Image();
      i.onload = () => { this.loaded += 1; };
      i.src = src;
    });
  }

  // Rotation loop. In 'auto' mode every viewer reads the same clock, so all the
  // pouches on a page (and across pages) show the same angle at the same time.
  tick() {
    if (this.paused || this.dragging || this.onScreen === false || this.staticSrc) return;
    const now = Date.now();
    if (this.mode === 'auto') {
      const synced = Math.floor(now / this.speed) * this.spinDir;
      if (((synced % this.frames.length) + this.frames.length) % this.frames.length !== this.frameIndex) this.setFrame(synced);
      return;
    }
    if (now - this.lastTick < this.speed) return;
    this.lastTick = now;
    this.setFrame(this.frameIndex + this.spinDir);
  }

  setFrame(i) {
    const total = this.frames.length;
    this.frameIndex = ((i % total) + total) % total;
    this.img.src = this.frames[this.frameIndex];
    if (this.fill) {
      this.fill.style.width = ((this.frameIndex / (total - 1 || 1)) * 100) + '%';
    }
  }

  onDown(e) {
    // Arm a potential drag, but DON'T commit or capture the pointer yet — we
    // first watch the direction so a vertical page-scroll that happens to
    // start on the product doesn't get hijacked into a spin (mobile fix).
    this.dragging = true;
    this.decided = false;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.pointerId = e.pointerId;
    this.staticSrc = null;
    clearInterval(this.settleT);
    clearTimeout(this.resetT);
    this.preload();
  }

  onMove(e) {
    if (!this.dragging) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;

    if (!this.decided) {
      // Wait until the gesture shows clear intent.
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        // Mostly vertical → it's a scroll, not a spin. Let the page scroll.
        this.dragging = false;
        return;
      }
      // Horizontal intent — now it's a real spin drag.
      this.decided = true;
      if (this.container.setPointerCapture) {
        try { this.container.setPointerCapture(this.pointerId); } catch (err) {}
      }
    }

    if (Math.abs(dx) > 10) {
      // Drag right → forward, drag left → back.
      this.setFrame(this.frameIndex + (dx > 0 ? 1 : -1));
      this.startX = e.clientX;
    }
  }

  onUp() {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.decided) {
      this.justDragged = true;
      setTimeout(() => { this.justDragged = false; }, 300);
    }
    if (this.mode === 'hover') {
      // Keep spinning only while the pointer is still over the product.
      this.paused = !this.hovered;
      if (!this.hovered) this.settleToStart();
    } else if (this.mode === 'auto') {
      clearTimeout(this.resumeT);
      this.paused = true;
      this.resumeT = setTimeout(() => { this.paused = false; }, 1200);
    }
  }

  // 'hover' mode: hovering spins the product, leaving settles it back to the
  // front-facing frame. 'auto' mode keeps turning regardless of hover.
  onEnter() {
    this.hovered = true;
    this.preload();
    if (this.mode === 'hover') {
      clearTimeout(this.resetT);
      this.paused = false;
    }
  }

  onLeave() {
    this.hovered = false;
    if (this.dragging) return;
    if (this.mode === 'hover') {
      this.paused = true;
      // Ease back to the hero frame so cards rest on a clean front shot.
      clearTimeout(this.resetT);
      this.resetT = setTimeout(() => {
        if (!this.hovered && !this.dragging) this.settleToStart();
      }, 260);
    }
  }

  // Animate back to frame 0 by the shortest direction.
  settleToStart() {
    if (this.frameIndex === 0) return;
    clearInterval(this.settleT);
    const total = this.frames.length;
    const step = this.frameIndex > total / 2 ? 1 : -1;
    this.settleT = setInterval(() => {
      if (this.hovered || this.dragging || this.frameIndex === 0) {
        clearInterval(this.settleT);
        return;
      }
      this.setFrame(this.frameIndex + step);
    }, 40);
  }

  // Used by the PDP thumbnail gallery to swap in a static photo.
  showStatic(src) {
    this.dragging = false;
    this.staticSrc = src;
    this.img.src = src;
  }

  // Restore the spin sequence after viewing a static photo.
  showSpin() {
    this.staticSrc = null;
    if (this.frames.length) this.setFrame(this.frameIndex);
  }
}

customElements.define('spin-viewer', SpinViewer);
